// The sidebar's pages and links, and who gets to see each one. The pages the
// app ships with are listed here with their defaults; admins change the
// labels, order, audience and visibility, and add their own links, from
// Admin → Navigation. Those choices are saved as one JSON value in the
// "Navigation" row of the Champion Settings list (everyone can read it; only
// champions and admins can write it in SharePoint, and only admins get the
// editor in the app).
//
// This decides what the app shows and which pages it opens. The data behind
// the pages is still protected by SharePoint's own permissions.
import {
  Activity,
  Award,
  BarChart3,
  BookOpen,
  Calendar,
  FileText,
  Globe,
  GraduationCap,
  Layers,
  LayoutDashboard,
  Lightbulb,
  Link as LinkIcon,
  Megaphone,
  MessageSquare,
  Rocket,
  Sparkles,
  Users,
  Video,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { logActivity } from "../api/activityLog";
import { createListItem, GRAPH_BASE, SITE_ID, updateListItem } from "../api/graph";
import { CHAMPION_LISTS } from "../api/champions";

export const NAV_ICONS: Record<string, { icon: LucideIcon; label: string }> = {
  dashboard: { icon: LayoutDashboard, label: "Dashboard" },
  book: { icon: BookOpen, label: "Book" },
  lightbulb: { icon: Lightbulb, label: "Idea" },
  layers: { icon: Layers, label: "Layers" },
  training: { icon: GraduationCap, label: "Training" },
  award: { icon: Award, label: "Award" },
  activity: { icon: Activity, label: "Activity" },
  chart: { icon: BarChart3, label: "Chart" },
  link: { icon: LinkIcon, label: "Link" },
  globe: { icon: Globe, label: "Website" },
  file: { icon: FileText, label: "Document" },
  sparkles: { icon: Sparkles, label: "Sparkles" },
  users: { icon: Users, label: "People" },
  video: { icon: Video, label: "Video" },
  megaphone: { icon: Megaphone, label: "News" },
  calendar: { icon: Calendar, label: "Calendar" },
  chat: { icon: MessageSquare, label: "Chat" },
  rocket: { icon: Rocket, label: "Launch" },
};
export const iconFor = (key: string): LucideIcon => NAV_ICONS[key]?.icon ?? LinkIcon;

/** Who sees an item. Admins always see everything, so they can't lock themselves out. */
export type NavAudience = "everyone" | "champions" | "admins";
export const AUDIENCES: { value: NavAudience; label: string; hint: string }[] = [
  { value: "everyone", label: "Everyone", hint: "Everyone who signs in." },
  { value: "champions", label: "Champions", hint: "AI Champions and admins." },
  { value: "admins", label: "Admins only", hint: "Only admins." },
];

export type NavEntry = {
  id: string;
  label: string;
  icon: string;
  /** An app page (built-in items only). */
  path?: string;
  /** An outside link: added links, and built-in items that open another site. */
  href?: string;
  /** In the sidebar. When off, the page still opens for its audience by its address. */
  onMenu: boolean;
  audience: NavAudience;
  /** Extra people (email addresses) who see it whatever the audience. */
  people: string[];
  builtIn: boolean;
  /** The dashboard: everyone keeps it, since it's where the app sends people. */
  locked?: boolean;
};

export type NavConfig = NavEntry[];

/** The app's own pages, in their default order, with their default audience. */
export const BUILT_IN: NavEntry[] = [
  { id: "/", path: "/", label: "Dashboard", icon: "dashboard", onMenu: true, audience: "everyone", people: [], builtIn: true, locked: true },
  { id: "/dashboard-new", path: "/dashboard-new", label: "Dashboard (New)", icon: "dashboard", onMenu: true, audience: "champions", people: [], builtIn: true },
  { id: "/library", path: "/library", label: "AI Library", icon: "book", onMenu: true, audience: "everyone", people: [], builtIn: true },
  { id: "/initiatives", path: "/initiatives", label: "AI Initiatives", icon: "lightbulb", onMenu: true, audience: "everyone", people: [], builtIn: true },
  { id: "/platforms", path: "/platforms", label: "AI Platforms", icon: "layers", onMenu: true, audience: "everyone", people: [], builtIn: true },
  { id: "/training", path: "/training", href: "https://app.sectionai.com/platform", label: "AI Training", icon: "training", onMenu: true, audience: "everyone", people: [], builtIn: true },
  { id: "/champions-new", path: "/champions-new", label: "AI Champions (New)", icon: "award", onMenu: true, audience: "champions", people: [], builtIn: true },
  { id: "/insights", path: "/insights", label: "Insights", icon: "chart", onMenu: false, audience: "everyone", people: [], builtIn: true },
  { id: "/activity", path: "/activity", label: "Activity Log", icon: "activity", onMenu: true, audience: "admins", people: [], builtIn: true },
];

export const NAV_SETTING = "Navigation";
const CACHE_KEY = "ainav.nav.v1";

/** What's saved: only what can be changed, so new defaults in code still reach saved items. */
type Stored = {
  id: string;
  label?: string;
  icon?: string;
  href?: string;
  onMenu?: boolean;
  audience?: NavAudience;
  people?: string[];
};

const isAudience = (v: unknown): v is NavAudience => v === "everyone" || v === "champions" || v === "admins";
const cleanPeople = (v: unknown) =>
  Array.isArray(v) ? v.map((x) => String(x).trim().toLowerCase()).filter((x) => x.includes("@")) : [];
const safeHref = (v: unknown) => {
  const s = typeof v === "string" ? v.trim() : "";
  return /^https:\/\//i.test(s) ? s : "";
};

/**
 * The saved list laid over the built-in pages: saved items keep their order
 * and settings, built-in pages that aren't in it yet (new in the app) are
 * added at the end with their defaults, and pages the app no longer has are
 * dropped.
 */
export function mergeConfig(stored: unknown): NavConfig {
  const items = Array.isArray(stored) ? (stored as Stored[]) : [];
  const out: NavEntry[] = [];
  const seen = new Set<string>();
  for (const s of items) {
    if (!s || typeof s.id !== "string" || seen.has(s.id)) continue;
    const base = BUILT_IN.find((b) => b.id === s.id);
    const label = typeof s.label === "string" && s.label.trim() ? s.label.trim().slice(0, 60) : "";
    const icon = typeof s.icon === "string" && NAV_ICONS[s.icon] ? s.icon : "";
    if (base) {
      out.push({
        ...base,
        label: label || base.label,
        icon: icon || base.icon,
        onMenu: typeof s.onMenu === "boolean" ? s.onMenu : base.onMenu,
        audience: base.locked ? "everyone" : isAudience(s.audience) ? s.audience : base.audience,
        people: base.locked ? [] : cleanPeople(s.people),
      });
    } else if (s.id.startsWith("link-")) {
      const href = safeHref(s.href);
      if (!href || !label) continue;
      out.push({
        id: s.id,
        label,
        icon: icon || "link",
        href,
        onMenu: s.onMenu !== false,
        audience: isAudience(s.audience) ? s.audience : "everyone",
        people: cleanPeople(s.people),
        builtIn: false,
      });
    } else continue;
    seen.add(s.id);
  }
  for (const b of BUILT_IN) if (!seen.has(b.id)) out.push({ ...b });
  return out;
}

export const defaultConfig = (): NavConfig => BUILT_IN.map((b) => ({ ...b, people: [] }));

function toStored(config: NavConfig): Stored[] {
  return config.map((e) => {
    const base = BUILT_IN.find((b) => b.id === e.id);
    const s: Stored = { id: e.id, onMenu: e.onMenu, audience: e.audience };
    if (!base || e.label !== base.label) s.label = e.label;
    if (!base || e.icon !== base.icon) s.icon = e.icon;
    if (!base) s.href = e.href;
    if (e.people.length) s.people = e.people;
    return s;
  });
}

export type Viewer = { email: string; isAdmin: boolean; isChampion: boolean };

export function canSee(entry: NavEntry, me: Viewer): boolean {
  if (me.isAdmin || entry.audience === "everyone") return true;
  if (entry.audience === "champions" && me.isChampion) return true;
  return Boolean(me.email) && entry.people.includes(me.email.toLowerCase());
}

/** Plain-words summary of who sees an item, for the admin list. */
export function audienceText(entry: NavEntry): string {
  const who = AUDIENCES.find((a) => a.value === entry.audience)?.label ?? entry.audience;
  if (entry.audience === "everyone" || !entry.people.length) return who;
  return `${who} + ${entry.people.length} ${entry.people.length === 1 ? "person" : "people"}`;
}

// ---------- Loading and saving ----------

export function readCachedConfig(): NavConfig | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? mergeConfig(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

function cache(stored: Stored[] | null) {
  try {
    if (stored) localStorage.setItem(CACHE_KEY, JSON.stringify(stored));
    else localStorage.removeItem(CACHE_KEY);
  } catch {
    /* not cached, that's fine */
  }
}

async function findSettingRow(token: string): Promise<{ id: string; value: string } | null> {
  let url: string | undefined = `${GRAPH_BASE}/sites/${SITE_ID}/lists/${CHAMPION_LISTS.settings}/items?expand=fields(select=Title,Value)&$top=200`;
  while (url) {
    const res: Response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error(`Couldn't load the navigation settings (${res.status}).`);
    const data = (await res.json()) as {
      value: { id: string; fields?: { Title?: string; Value?: string } }[];
      "@odata.nextLink"?: string;
    };
    const row = data.value.find((r) => (r.fields?.Title ?? "").trim() === NAV_SETTING);
    if (row) return { id: row.id, value: row.fields?.Value ?? "" };
    url = data["@odata.nextLink"];
  }
  return null;
}

/** The saved navigation, or the defaults when nothing has been saved. */
export async function fetchNavConfig(token: string): Promise<NavConfig> {
  const row = await findSettingRow(token);
  let stored: Stored[] | null = null;
  if (row?.value.trim()) {
    try {
      const parsed = JSON.parse(row.value) as { items?: Stored[] };
      stored = Array.isArray(parsed.items) ? parsed.items : null;
    } catch {
      stored = null;
    }
  }
  cache(stored);
  return stored ? mergeConfig(stored) : defaultConfig();
}

/** Saves the navigation; null goes back to the app's defaults. */
export async function saveNavConfig(token: string, config: NavConfig | null): Promise<NavConfig> {
  const stored = config ? toStored(config) : null;
  const value = stored ? JSON.stringify({ v: 1, items: stored }) : "";
  const row = await findSettingRow(token);
  if (row) await updateListItem(token, CHAMPION_LISTS.settings, row.id, { Value: value });
  else if (stored) await createListItem(token, CHAMPION_LISTS.settings, { Title: NAV_SETTING, Value: value });
  cache(stored);
  void logActivity("Feature", config ? "NavigationUpdated" : "NavigationReset", config ? "Updated the navigation" : "Reset the navigation to defaults");
  return stored ? mergeConfig(stored) : defaultConfig();
}
